import { UploadDropZone } from '@rpldy/upload-drop-zone';
import { FileFilterMethod, useBatchFinishListener, useUploady } from '@rpldy/uploady';
import clsx from 'clsx';
import { useState } from 'react';
import { Area } from 'react-easy-crop';

import attachmentGlowIcon from '../../../../assets/images/icons/attachment-glow.svg';
import checkboxCheckedIcon from '../../../../assets/images/icons/checkbox-checked.png';
import cloudUploadIcon from '../../../../assets/images/icons/cloud-upload-icon.svg';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Avatar from '../../../../components/common/Avatar';
import CropContainer from '../../../../components/common/CropContainer';
import { useAppSelector } from '../../../../store';
import { getCroppedImg } from '../../../../utils/avatarSectionHelpers';
import {
  selectAccountState,
  user,
  setNewAvatarUploaded,
  setAvatarDeleted,
} from '../../../account/api/accountSlice';
import CustomModal from '../../../app/components/CustomModal';
import { returnFileSize } from '../../../questionnaire/utils/returnFileSize';
import alert from '../../../../utils/alert';

const MAX_FILE_SIZE = 10000000; // 10 MB

const AvatarSection = (): JSX.Element => {
  const { avatar, avatarDeleted } = useAppSelector(selectAccountState);
  const userInfo = useAppSelector(user);
  const { t } = useTranslation();
  const { showFileUpload } = useUploady();
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();
  const [avatarPreview, setAvatarPreview] = useState<string>('');

  useBatchFinishListener((batch) => {
    setUploadedFile(batch.items?.[0]?.file as File);
  });

  const fileFilter: FileFilterMethod = (file) => {
    if (typeof file !== 'string') {
      const allowedTypes = ['image/jpeg', 'image/png'];
      let canUpload = allowedTypes.includes(file.type);
      canUpload = canUpload && file.size < MAX_FILE_SIZE;
      return canUpload;
    }
    return false;
  };

  const onConfirm = async () => {
    try {
      if (croppedAreaPixels) {
        await Promise.all([
          dispatch(
            setNewAvatarUploaded({
              newAvatar: true,
              cropped: true,
              croppedArea: croppedAreaPixels,
            }),
          ),
          dispatch(
            setAvatarDeleted({
              avatarDeleted: false,
            }),
          ),
        ]);
      }
      // SHOW TEMPORARY HOW THE NEW AVATAR WILL LOOK
      // IN ACCOUNT PAGE
      if (uploadedFile && croppedAreaPixels) {
        const croppedAvatarImage = await getCroppedImg(
          URL.createObjectURL(uploadedFile),
          croppedAreaPixels,
        );
        croppedAvatarImage && setAvatarPreview(URL.createObjectURL(croppedAvatarImage));
      }
    } catch (e) {
      alert.error(t('Something went wrong. Please try again.'));
    } finally {
      setShowModal(false);
      setUploadedFile(null);
    }
  };

  const onCancel = () => {
    setShowModal(false);
    setUploadedFile(null);
  };

  const onDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setAvatarPreview('');
    await Promise.all([
      dispatch(
        setNewAvatarUploaded({
          newAvatar: false,
          cropped: false,
          croppedArea: null,
        }),
      ),
      dispatch(
        setAvatarDeleted({
          avatarDeleted: true,
        }),
      ),
    ]);
  };

  return (
    <>
      <div className='col-md-6 col-lg-4'>
        <div className='d-flex justify-content-between flex-row mb-3'>
          <p className='form-label m-0'>{t('Avatar')}</p>
          <div>
            <button
              className={clsx(
                'p-0 m-0 h-100 page__subsection-link form-label',
                `${avatar ? 'text-danger' : 'text-gray-300'}  `,
              )}
              disabled={avatar ? false : true}
              onClick={onDelete}
            >
              {t('Delete')}
            </button>
            <button
              className={clsx(
                'p-0 m-0 ms-3 h-100 page__subsection-link form-label',
                'text-primary-700',
              )}
              onClick={(e) => {
                e.preventDefault();
                setShowModal(true);
              }}
              type='button'
            >
              {t('Edit')}
            </button>
          </div>
        </div>

        <Avatar
          width={64}
          height={64}
          src={!avatarDeleted ? avatarPreview || avatar?.url : undefined}
          fallbackString={userInfo?.name?.[0]?.given?.[0] || userInfo?.name?.[0]?.family}
        />
      </div>
      <CustomModal
        show={showModal}
        title={t('Upload Avatar')}
        confirmText={t('Done')}
        onHide={onCancel}
        handleCancel={onCancel}
        handleConfirm={onConfirm}
      >
        {!uploadedFile ? (
          <UploadDropZone
            id='upload-logo-dropzone'
            className='custom__upload-modal-dropzone'
            onDragOverClassName='custom__upload-modal-dropzone--dragover'
            fileFilter={fileFilter}
          >
            <img src={cloudUploadIcon} alt='upload indicator' />
            <div className='d-flex flex-column justify-content-center'>
              <button
                className='text-primary-700 m-0 p-0 bg-transparent border-0'
                onClick={() =>
                  showFileUpload({
                    clearPendingOnAdd: true,
                    maxGroupSize: 1,
                    fileFilter,
                    inputFieldName: 'logo-upload-btn',
                  })
                }
              >
                {t('Click to upload')}
              </button>
              <div>
                <p className='m-0 text-center'>{t('or drag and drop')}</p>
                <p className='text-gray-500 m-0 text-center'>{t('PNG or JPG, max. 10 MB')}</p>
              </div>
            </div>
          </UploadDropZone>
        ) : (
          <CropContainer
            image={URL.createObjectURL(uploadedFile as any)}
            onCropComplete={(croppedArea: Area, croppedAreaPixels: Area) => {
              setCroppedAreaPixels(croppedAreaPixels);
            }}
          />
        )}

        {uploadedFile && (
          <div className='custom__upload-modal-file-container'>
            <img src={attachmentGlowIcon} alt='attachment icon' />
            <div className='flex-grow-1 ps-2'>
              <p className='text-gray-700 mb-0'>{uploadedFile.name}</p>
              <p className='text-gray-500 mb-0'>{returnFileSize(+uploadedFile.size)}</p>
            </div>
            <img height={16} src={checkboxCheckedIcon} alt='checked checkbox icon' />
          </div>
        )}
      </CustomModal>
    </>
  );
};

export default AvatarSection;
